import useGetApi from "../useGetApi"
import useMutateApi from "../useMutateApi"
import getUrl from "api/apiMap"
import apiEndpoints from "api/utils/apiEndpoints"
import queryKeys from "api/utils/queryKeys"
import useAxiosPrivate from "../useAxiosPrivate"
import { EMA_RWD_OPTIONS, EMA_RWD_OPTIONS_TYPE_MAP } from "constants/authoring"
import { getLocalStorage, setLocalStorage } from "api/apiUtils"

/** TODO: move the following to authoring/studiesApis.js */

// NOTE: use dropdown useGetStudiesList instead
export const useGetAuthoringStudies = () => {
  return useGetApi(getUrl(apiEndpoints.authoring), queryKeys.authoringStudies.all)
}

export const useGetAuthoringStudy = (authoringId, queryParams) => {
  let query = ""
  if (queryParams) query = "?" + new URLSearchParams(queryParams)?.toString()
  return useGetApi(
    getUrl(`${apiEndpoints.authoring}${Number(authoringId)}/${query ? query : ""}`),
    queryKeys.authoringStudies.detail(Number(authoringId))
  )
}

export const usePutAuthoringStudy = (authoringId) => {
  return useMutateApi({
    method: "put",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}`),
    invalidateQueries: [queryKeys.authoringVersions.list(Number(authoringId))],
    // successMsg: 'Successfully updated study.'
  })
}

// unlock form
export const useUnlockAuthoringForm = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/unlock/`),
    invalidateQueries: [queryKeys.authoringUnlock(Number(authoringId))],
    successMsg: "Successfully unlocked form.",
  })
}

/** versions */

export const useGetAuthoringVersions = (authoringId, queryOptions) => {
  return useGetApi(
    getUrl(`${apiEndpoints.authoring}${authoringId}/versions/`),
    queryKeys.authoringVersions.list(Number(authoringId)),
    { select: (data) => data?.versions },
    queryOptions
  )
}

export const useGetAuthoringApprovedVersions = (authoringId, queryOptions) => {
  return useGetApi(
    getUrl(`${apiEndpoints.authoring}${authoringId}/approved-versions/`),
    queryKeys.authoringVersions.approvedVersions(Number(authoringId)),
    queryOptions
  )
}

export const useGetAuthoringVersion = (authoringId, verNum, queryOptions) => {
  return useGetApi(
    getUrl(`${apiEndpoints.authoring}${authoringId}/versions/${verNum}/`),
    queryKeys.authoringVersions.detail(verNum),
    queryOptions
  )
}

/** TODO: move following to authoring/studyCommentsApis.js */

export const useGetAuthoringComments = (authoringId) => {
  return useGetApi(
    getUrl(`${apiEndpoints.authoring}${authoringId}/comments/`),
    queryKeys.authoringComments.list(Number(authoringId))
  )
}

export const usePostAuthoringComment = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/comments/`),
    invalidateQueries: [queryKeys.authoringComments.list(Number(authoringId))],
  })
}

export const usePutAuthoringComment = (authoringId) => {
  return useMutateApi({
    method: "put",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/comments/`),
    invalidateQueries: [queryKeys.authoringComments.list(Number(authoringId))],
  })
}

export const useDeleteAuthoringComment = (authoringId) => {
  return useMutateApi({
    method: "delete",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/comments/`),
    invalidateQueries: [queryKeys.authoringComments.detail(Number(authoringId))],
  })
}

export const useFavoriteAuthoringComment = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/comments/`),
    invalidateQueries: [
      queryKeys.authoringComments.detail(Number(authoringId)),
      queryKeys.authoringComments.list(Number(authoringId)),
    ],
  })
}

export const useResolveAuthoringComment = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/comments/`),
    invalidateQueries: [
      queryKeys.authoringComments.detail(Number(authoringId)),
      queryKeys.authoringComments.list(Number(authoringId)),
    ],
  })
}

/** move following to authoring/submissionsApis.js */

// prs preview
export const usePostPrsPreview = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}`),
    invalidateQueries: [queryKeys.prsPreview.details(Number(authoringId))],
  })
}

// qa comments report
export const usePostQaCommentsPage = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/qa-comments-page`),
    invalidateQueries: [queryKeys.qaComments.details(Number(authoringId))],
  })
}

// registry validations
export const usePostRegistryValidations = (authoringId) => {
  return useMutateApi({
    method: "post",
    apiEndpoint: getUrl(`${apiEndpoints.authoring}${authoringId}/validate`),
    invalidateQueries: [queryKeys.authoringValidations.details(Number(authoringId))],
  })
}

/** TODO: move the following to dropdown/dropdownApis.js */

export const useGetProgramIDList = () => {
  return useGetApi(getUrl(apiEndpoints.programId), queryKeys.programId)
}

export const useGetProgramNameList = () => {
  return useGetApi(getUrl(apiEndpoints.programName), queryKeys.programName)
}

export const useJrctCsvDownload = () => {
  const axios = useAxiosPrivate()

  const getJrctCsvDownload = async (authoringId, formSection) => {
    return axios
      .post(getUrl(`/authoring/api/v1/studies/${authoringId}/jrct-csv-download/`), {
        csv_type: formSection,
      })
      .then((resp) => {
        return resp.data
      })
  }

  return { getJrctCsvDownload }
}

// types ["institutions", "data_source", "network", "atc_code", "substance_inn", "medicine", "condition"]
export const useEmaRwdDropdowns = () => {
  const axios = useAxiosPrivate()

  const fetchItems = async (type) => {
    return await axios.get(getUrl(`/authoring/api/v1/studies/ema-rwd-catalgoues`), {
      params: {
        dropdown_type: type,
      },
    })
  }

  const getEmaRwdOptions = async (jpath) => {
    const data = getLocalStorage(EMA_RWD_OPTIONS[EMA_RWD_OPTIONS_TYPE_MAP[jpath]])

    if (data) {
      return data
    } else {
      return await fetchItems(EMA_RWD_OPTIONS_TYPE_MAP[jpath]).then((res) => {
        const items = res.data.catalogue_values.map((item) => ({ value: item, label: item }))
        setLocalStorage(EMA_RWD_OPTIONS[EMA_RWD_OPTIONS_TYPE_MAP[jpath]], items, 1)
        return items
      })
    }
  }
  return {
    getEmaRwdOptions,
  }
}
